import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import ApproveRequestModal from './components/ui/ApproveRequestModal'
import { useDispatch, useSelector } from 'react-redux'
import {
  deleteSpecialPurchase,
  fetchAllSpecialPurchases,
  fetchDepartments,
} from 'src/features/SpecialPurchasesSlice'
import { Table, Tag, Input, Checkbox, Popconfirm, Button, Modal } from 'antd'
import dayjs from 'dayjs'
import SpecialPurchasesCSV from './components/ui/SpecialPurchasesCSV'

const { Search } = Input
import SpecialPurchaseLimit from './SpecialPurchaseLimit'
import { stickyActionsColumn } from 'src/components/styled/horizontalScroll'
import { DeleteOutlined, EyeOutlined } from '@ant-design/icons'
import {
  FoodSafetyHeaderRow,
  FoodSafetyHeroBand,
  FoodSafetyPageTitle,
} from 'src/components/food-safety/FoodSafetyPageChrome'
import { resolveEffectiveVenuePlan } from 'src/utils/invoiceUploadQueueHelpers'
import { RESPONSIVE_TABLE_SCROLL } from 'src/utils/responsiveTable'

const SpecialPurchases = () => {
  const user = useSelector(state => state.auth)
  const isAdmin =
    user?.account?.isAdmin ||
    user?.account?.TFRAdmin ||
    user?.account?.GREENAdmin ||
    user?.account?.JUSTAdmin

  const dispatch = useDispatch()
  const [approveReqeustModalVisible, setApproveReqestModalVisible] =
    useState(false)
  const [viewRequestModalVisible, setViewRequestModalVisible] = useState(false)
  const [selectedPurchase, setSelectedPurchase] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [statusFilters, setStatusFilters] = useState({
    approved: true,
    declined: true,
    pending: true,
  })
  const { isLoading, purchases } = useSelector(state => state.purchases)
  const departments = useSelector(state => state.purchases.departments)
  const venueId = useSelector(state => state.venue?.venue?._id)
  const venue = useSelector(state => state.venue?.venue)
  const currentPlan = resolveEffectiveVenuePlan({
    newPricing: venue?.newPricing,
    usageLimits: venue?.usageLimits,
    currentPlan: venue?.usageLimits?.currentPlan,
  })

  useEffect(() => {
    if (venueId && currentPlan === 'Pro') {
      dispatch(fetchAllSpecialPurchases(venueId))
      dispatch(fetchDepartments(venueId))
    }
  }, [dispatch, venueId, currentPlan])

  const toSearchString = value => (value ?? '').toString().toLowerCase()

  const getDepartmentName = department => {
    if (!department) return 'Unknown'
    if (typeof department === 'object' && department.name) {
      return department.name
    }
    const found = departments.find(dept => dept._id === department)
    return found ? found.name : String(department)
  }

  const filteredPurchases = purchases.filter(purchase => {
    const query = toSearchString(searchText)

    const matchesSearch =
      toSearchString(purchase.managerDetails?.managerName).includes(query) ||
      toSearchString(purchase.managerDetails?.managerEmail).includes(query) ||
      toSearchString(purchase.staffDetails?.staffName).includes(query) ||
      toSearchString(purchase.staffDetails?.staffEmail).includes(query) ||
      toSearchString(purchase.itemDetails?.itemName).includes(query) ||
      toSearchString(getDepartmentName(purchase.department)).includes(query) ||
      toSearchString(purchase.referenceNumber).includes(query) ||
      toSearchString(purchase.itemDetails?.itemCost).includes(query)

    const matchesStatus =
      (statusFilters.approved && purchase.isApproved) ||
      (statusFilters.declined && purchase.isDeclined) ||
      (statusFilters.pending && !purchase.isApproved && !purchase.isDeclined)

    return matchesSearch && matchesStatus
  })

  const columns = [
    {
      title: 'Reference ID',
      dataIndex: 'referenceNumber',
      key: 'referenceNumber',
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: text => text?.name ?? 'N/A',
    },
    {
      title: 'Manager Name',
      dataIndex: ['managerDetails', 'managerName'],
      key: 'managerName',
    },
    {
      title: 'Manager Email',
      dataIndex: ['managerDetails', 'managerEmail'],
      key: 'managerEmail',
    },
    {
      title: 'Staff Name',
      dataIndex: ['staffDetails', 'staffName'],
      key: 'staffName',
    },
    {
      title: 'Staff Email',
      dataIndex: ['staffDetails', 'staffEmail'],
      key: 'staffEmail',
    },
    {
      title: 'Item Name',
      dataIndex: ['itemDetails', 'itemName'],
      key: 'itemName',
    },
    {
      title: 'Item Cost (£)',
      dataIndex: ['itemDetails', 'itemCost'],
      key: 'itemCost',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: text => dayjs(text).format('YYYY-MM-DD'),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <>
          {record.isApproved ? (
            <Tag color='green'>Approved</Tag>
          ) : record.isDeclined ? (
            <Tag color='red'>Declined</Tag>
          ) : (
            <Tag color='orange'>Pending</Tag>
          )}
        </>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      className: 'sticky-actions-col',
      onCell: () => ({ className: 'sticky-actions-cell' }),
      render: (_, record) => (
        <ActionsWrapper>
          <Button
            type='primary'
            onClick={() => {
              setSelectedPurchase(record)
              setViewRequestModalVisible(true)
            }}
          >
            <EyeOutlined />
          </Button>
          {isAdmin && (
            <Popconfirm
              title='Are you sure to delete this purchase?'
              placement='topRight'
              onConfirm={() => {
                dispatch(deleteSpecialPurchase(record._id, venueId))
              }}
              okText='Yes'
              cancelText='No'
            >
              <Button type={'danger'}>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </ActionsWrapper>
      ),
    },
  ]

  const handleStatusFilterChange = (status, checked) => {
    setStatusFilters(prev => ({
      ...prev,
      [status]: checked,
    }))
  }
  if (currentPlan !== 'Pro') {
    return <SpecialPurchaseLimit />
  } else {
    return (
      <div>
        <ApproveRequestModal
          approveReqeustModalVisible={approveReqeustModalVisible}
          setApproveReqestModalVisible={setApproveReqestModalVisible}
        />
        <FoodSafetyHeroBand>
          <FoodSafetyHeaderRow>
            <div>
              <FoodSafetyPageTitle>
                Manage Special Purchases
              </FoodSafetyPageTitle>
              <HeroIntro>
                Track any one-off or ad-hoc purchases that fall outside of your
                regular supplier orders.
              </HeroIntro>
            </div>
            <HeroActions>
              <Button
                type='primary'
                onClick={() => setApproveReqestModalVisible(true)}
              >
                Request Manager Approval
              </Button>
              <SpecialPurchasesCSV specialPurchases={filteredPurchases} />
            </HeroActions>
          </FoodSafetyHeaderRow>
          <HeroNote>
            <strong>Important.</strong> Special Purchases are not included in
            your GP% or in your ordered products/purchasing stats.
          </HeroNote>
          <HeroHelp>
            <strong>This tool helps you:</strong>
            <ul>
              <li>Record occasional or one-off purchases</li>
              <li>Keep your reporting accurate</li>
              <li>Track spend across categories</li>
            </ul>
          </HeroHelp>
        </FoodSafetyHeroBand>

        <SearchContainer>
          <Search
            placeholder='Search by any field...'
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 400, marginBottom: 16 }}
          />

          <CheckboxGroup>
            <Checkbox
              checked={statusFilters.approved}
              onChange={e =>
                handleStatusFilterChange('approved', e.target.checked)
              }
            >
              Approved
            </Checkbox>
            <Checkbox
              checked={statusFilters.declined}
              onChange={e =>
                handleStatusFilterChange('declined', e.target.checked)
              }
            >
              Declined
            </Checkbox>
            <Checkbox
              checked={statusFilters.pending}
              onChange={e =>
                handleStatusFilterChange('pending', e.target.checked)
              }
            >
              Pending
            </Checkbox>
          </CheckboxGroup>
        </SearchContainer>

        <TableWrap>
          <Table
            scroll={RESPONSIVE_TABLE_SCROLL}
            loading={isLoading}
            style={{
              marginTop: 20,
            }}
            dataSource={filteredPurchases}
            columns={columns}
            rowKey='_id'
          />
        </TableWrap>

        <Modal
          visible={viewRequestModalVisible}
          title='Special Purchase Details'
          onCancel={() => {
            setViewRequestModalVisible(false)
            setSelectedPurchase(null)
          }}
          footer={[
            <Button
              key='close'
              type='primary'
              onClick={() => {
                setViewRequestModalVisible(false)
                setSelectedPurchase(null)
              }}
            >
              Close
            </Button>,
          ]}
        >
          {selectedPurchase && (
            <ModalContent>
              <Section>
                <SectionTitle>Reference</SectionTitle>
                <DetailRow>
                  <strong>Reference ID:</strong>{' '}
                  {selectedPurchase.referenceNumber || 'N/A'}
                </DetailRow>
                <DetailRow>
                  <strong>Created:</strong>{' '}
                  {selectedPurchase.createdAt
                    ? dayjs(selectedPurchase.createdAt).format('YYYY-MM-DD')
                    : 'N/A'}
                </DetailRow>
                <DetailRow>
                  <strong>Status:</strong>{' '}
                  {selectedPurchase.isApproved
                    ? 'Approved'
                    : selectedPurchase.isDeclined
                    ? 'Declined'
                    : 'Pending'}
                </DetailRow>
                {selectedPurchase.declineReason ? (
                  <DetailRow>
                    <strong>Decline reason:</strong>{' '}
                    {selectedPurchase.declineReason}
                  </DetailRow>
                ) : null}
              </Section>

              <Section>
                <SectionTitle>People</SectionTitle>
                <DetailRow>
                  <strong>Manager:</strong>{' '}
                  {selectedPurchase.managerDetails?.managerName || 'N/A'}
                  {selectedPurchase.managerDetails?.managerEmail
                    ? ` (${selectedPurchase.managerDetails.managerEmail})`
                    : ''}
                </DetailRow>
                <DetailRow>
                  <strong>Staff:</strong>{' '}
                  {selectedPurchase.staffDetails?.staffName || 'N/A'}
                  {selectedPurchase.staffDetails?.staffEmail
                    ? ` (${selectedPurchase.staffDetails.staffEmail})`
                    : ''}
                </DetailRow>
                <DetailRow>
                  <strong>Department:</strong>{' '}
                  {selectedPurchase.department?.name ||
                    getDepartmentName(selectedPurchase.department) ||
                    'N/A'}
                </DetailRow>
              </Section>

              <Section>
                <SectionTitle>Item</SectionTitle>
                <DetailRow>
                  <strong>Supplier:</strong>{' '}
                  {selectedPurchase.supplierDetails?.title || 'N/A'}
                </DetailRow>
                <DetailRow>
                  <strong>SKU:</strong>{' '}
                  {selectedPurchase.itemDetails?.sku || 'N/A'}
                </DetailRow>
                <DetailRow>
                  <strong>Item:</strong>{' '}
                  {selectedPurchase.itemDetails?.itemName || 'N/A'}
                </DetailRow>
                <DetailRow>
                  <strong>Cost:</strong>{' '}
                  {selectedPurchase.itemDetails?.itemCost != null
                    ? `£${selectedPurchase.itemDetails.itemCost}`
                    : 'N/A'}
                </DetailRow>
              </Section>

              <Section>
                <SectionTitle>Justification</SectionTitle>
                <JustificationBox>
                  {selectedPurchase.justification?.trim()
                    ? selectedPurchase.justification
                    : 'No justification provided.'}
                </JustificationBox>
              </Section>
            </ModalContent>
          )}
        </Modal>
      </div>
    )
  }
}

const HeroIntro = styled.p`
  margin: 8px 0 0;
  color: #555;
  font-size: 0.95rem;
  line-height: 1.45;
`

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const HeroNote = styled.p`
  margin: 16px 0 0;
  color: #132d07;
  line-height: 1.45;
`

const HeroHelp = styled.div`
  margin-top: 12px;
  color: #555;
  line-height: 1.45;

  ul {
    margin: 8px 0 0 18px;
  }
`

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`

const CheckboxGroup = styled.div`
  display: flex;
  gap: 16px;
`

const TableWrap = styled.div`
  ${stickyActionsColumn}
  width: 100%;
  min-width: 0;
  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    white-space: nowrap;
  }
`

const ActionsWrapper = styled.div`
  display: flex;
  gap: 8px;
`

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  color: ${({ theme }) => theme.colours.charcoal};
`

const DetailRow = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colours.oliveGrey};
  line-height: 1.5;
`

const JustificationBox = styled.div`
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  border-radius: 4px;
  padding: 12px;
  min-height: 80px;
  background: ${({ theme }) => theme.colours.mayoWhite};
  white-space: pre-wrap;
`

export default SpecialPurchases
